import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";
import { RefreshCw } from "lucide-react";

const DEFAULT_ACADEMIC_YEAR = "2026/2027";

const EMPTY = {
  matricola: "",
  first_name: "", last_name: "",
  date_of_birth: "", birth_place: "", gender: "",
  codice_fiscale: "",
  email: "", phone: "",
  address: "", city: "", country: "Italia",
  chiesa_provenienza: "",
  guardian_name: "", guardian_phone: "", guardian_email: "",
  enrollment_date: "",
  enrollment_year: "",
  academic_year: DEFAULT_ACADEMIC_YEAR,
  status: "attivo",
  course_track: "",
  track_type: "residenziale",
  year_level: "1",
  notes: "",
  dsa: false,
  has_insurance: false,
  health_notes: "",
  intolerances: "",
};

function normalize(s) {
  if (!s) return { ...EMPTY };
  return {
    matricola: s.matricola || "",
    first_name: s.first_name || "",
    last_name: s.last_name || "",
    date_of_birth: s.date_of_birth || "",
    birth_place: s.birth_place || "",
    gender: s.gender || "",
    codice_fiscale: s.codice_fiscale || "",
    email: s.email || "",
    phone: s.phone || "",
    address: s.address || "",
    city: s.city || "",
    country: s.country || "Italia",
    chiesa_provenienza: s.chiesa_provenienza || "",
    guardian_name: s.guardian_name || "",
    guardian_phone: s.guardian_phone || "",
    guardian_email: s.guardian_email || "",
    enrollment_date: s.enrollment_date || "",
    enrollment_year: s.enrollment_year ? String(s.enrollment_year) : "",
    academic_year: s.academic_year || DEFAULT_ACADEMIC_YEAR,
    status: s.status || "attivo",
    course_track: s.course_track || "",
    track_type: s.track_type || "residenziale",
    year_level: s.year_level ? String(s.year_level) : "1",
    notes: s.notes || "",
    dsa: !!s.dsa,
    has_insurance: !!s.has_insurance,
    health_notes: s.health_notes || "",
    intolerances: s.intolerances || "",
  };
}

export default function StudentFormDialog({ open, onOpenChange, mode = "create", initialStudent = null, onSubmit, submitting = false }) {
  const [form, setForm] = useState(() => normalize(initialStudent));
  const [nextMatricola, setNextMatricola] = useState("");

  useEffect(() => {
    if (open) setForm(normalize(initialStudent));
  }, [open, initialStudent]);

  // Anteprima matricola in create-mode: pre-compila con la prossima sequenziale (editabile)
  useEffect(() => {
    if (!open || mode !== "create") return;
    api.get("/students/-next-matricola").then((r) => {
      setNextMatricola(r.data?.next || "");
      setForm((f) => (f.matricola ? f : { ...f, matricola: r.data?.next || "" }));
    }).catch(() => {});
  }, [open, mode]);

  function submit(e) {
    e.preventDefault();
    const payload = { ...form };
    // coerce numerics
    if (payload.year_level === "") payload.year_level = null;
    else payload.year_level = parseInt(payload.year_level, 10);
    if (payload.enrollment_year === "") payload.enrollment_year = null;
    else payload.enrollment_year = parseInt(payload.enrollment_year, 10);
    // strip empty strings so backend keeps nulls (ma NON toccare matricola: se vuota, backend auto-genera)
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });
    payload.status = form.status || "attivo";
    onSubmit(payload);
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 flex flex-col gap-0">
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-stone-100">
          <DialogTitle>{mode === "edit" ? "Modifica studente" : "Nuovo studente"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Matricola</div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label>Numero matricola</Label>
              <Input
                data-testid="student-matricola"
                value={form.matricola}
                onChange={set("matricola")}
                className="mt-1.5 mono"
                placeholder={nextMatricola ? `Es. ${nextMatricola}` : "Auto-generata se vuota"}
              />
            </div>
            {mode === "create" && nextMatricola && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full mb-0.5"
                onClick={() => setForm({ ...form, matricola: nextMatricola })}
                data-testid="student-matricola-suggest"
                title="Usa la prossima matricola sequenziale"
              >
                <RefreshCw size={12} className="mr-1"/> Sequenziale
              </Button>
            )}
          </div>
          <p className="text-[11px] text-stone-500 -mt-2">
            {mode === "create"
              ? "Lascia vuoto per auto-generazione sequenziale, o inserisci manualmente."
              : "Modifica solo se necessario; deve essere unica."}
          </p>

          <div className="text-[10px] uppercase tracking-widest text-stone-500 pt-2">Anagrafica</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome</Label>
              <Input data-testid="student-first-name" required value={form.first_name} onChange={set("first_name")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Cognome</Label>
              <Input data-testid="student-last-name" required value={form.last_name} onChange={set("last_name")} className="mt-1.5"/>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Data di nascita</Label>
              <Input data-testid="student-dob" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Luogo di nascita</Label>
              <Input value={form.birth_place} onChange={set("birth_place")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Genere</Label>
              <Select value={form.gender || "unspecified"} onValueChange={(v) => setForm({ ...form, gender: v === "unspecified" ? "" : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Non specificato"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unspecified">Non specificato</SelectItem>
                  <SelectItem value="M">Maschio</SelectItem>
                  <SelectItem value="F">Femmina</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                  <SelectItem value="preferisco_non_dire">Preferisco non dirlo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Codice fiscale</Label>
              <Input value={form.codice_fiscale} onChange={set("codice_fiscale")} className="mt-1.5 mono uppercase" maxLength={16}/>
            </div>
            <div>
              <Label>Assicurazione</Label>
              <Select value={form.has_insurance ? "si" : "no"} onValueChange={(v) => setForm({ ...form, has_insurance: v === "si" })}>
                <SelectTrigger className="mt-1.5" data-testid="student-insurance"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="si" data-testid="student-insurance-si">Presente</SelectItem>
                  <SelectItem value="no" data-testid="student-insurance-no">Assente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Contatti</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input data-testid="student-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.phone} onChange={set("phone")} className="mt-1.5"/>
            </div>
          </div>
          <div>
            <Label>Indirizzo</Label>
            <Input value={form.address} onChange={set("address")} className="mt-1.5"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Città</Label>
              <Input value={form.city} onChange={set("city")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Paese</Label>
              <Input value={form.country} onChange={set("country")} className="mt-1.5"/>
            </div>
          </div>
          <div>
            <Label>Chiesa di provenienza</Label>
            <Input
              data-testid="student-chiesa"
              value={form.chiesa_provenienza}
              onChange={set("chiesa_provenienza")}
              placeholder="Es. Chiesa Evangelica di Milano"
              className="mt-1.5"
            />
          </div>

          <Separator />
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Contatto di riferimento (facoltativo)</div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Nome tutore/famiglia</Label>
              <Input value={form.guardian_name} onChange={set("guardian_name")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.guardian_phone} onChange={set("guardian_phone")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.guardian_email} onChange={set("guardian_email")} className="mt-1.5"/>
            </div>
          </div>

          <Separator />
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Percorso di studi</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipologia percorso</Label>
              <Select value={form.track_type || "unspecified"} onValueChange={(v) => setForm({ ...form, track_type: v === "unspecified" ? "" : v })}>
                <SelectTrigger data-testid="student-track-type" className="mt-1.5"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unspecified">Non specificato</SelectItem>
                  <SelectItem value="residenziale">Residenziale (3 anni)</SelectItem>
                  <SelectItem value="modulare">Modulare (7 anni)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Anno di corso (aula)</Label>
              <Select value={form.year_level || "unspecified"} onValueChange={(v) => setForm({ ...form, year_level: v === "unspecified" ? "" : v })}>
                <SelectTrigger data-testid="student-year-level" className="mt-1.5"><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unspecified">Non assegnato</SelectItem>
                  <SelectItem value="1">I anno</SelectItem>
                  <SelectItem value="2">II anno</SelectItem>
                  <SelectItem value="3">III anno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Indirizzo / specializzazione</Label>
              <Input value={form.course_track} onChange={set("course_track")} placeholder="Es. Missione estero" className="mt-1.5"/>
            </div>
            <div>
              <Label>Data iscrizione</Label>
              <Input type="date" value={form.enrollment_date} onChange={set("enrollment_date")} className="mt-1.5"/>
            </div>
            <div>
              <Label>Annualità</Label>
              <Select value={form.academic_year || DEFAULT_ACADEMIC_YEAR} onValueChange={(v) => setForm({ ...form, academic_year: v })}>
                <SelectTrigger className="mt-1.5" data-testid="student-academic-year"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024/2025">2024/2025</SelectItem>
                  <SelectItem value="2025/2026">2025/2026</SelectItem>
                  <SelectItem value="2026/2027">2026/2027 (corrente)</SelectItem>
                  <SelectItem value="2027/2028">2027/2028</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Stato</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="student-status" className="mt-1.5"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="attivo">Attivo</SelectItem>
                <SelectItem value="sospeso">Sospeso</SelectItem>
                <SelectItem value="ritirato">Ritirato</SelectItem>
                <SelectItem value="diplomato">Diplomato</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Note</Label>
            <Textarea rows={3} value={form.notes} onChange={set("notes")} className="mt-1.5"/>
          </div>

          <Separator />
          <div className="text-[10px] uppercase tracking-widest text-stone-500">Note sanitarie (riservate)</div>
          <div className="flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50/40 px-3 py-2">
            <input
              id="dsa-check"
              data-testid="student-dsa"
              type="checkbox"
              checked={!!form.dsa}
              onChange={(e) => setForm({ ...form, dsa: e.target.checked })}
              className="w-4 h-4 accent-[#1C352D]"
            />
            <label htmlFor="dsa-check" className="text-sm cursor-pointer">
              <span className="font-medium">DSA</span>
              <span className="text-stone-500 ml-1.5">(disturbo specifico dell'apprendimento)</span>
            </label>
          </div>
          <div>
            <Label>Note salute (patologie, terapie, allergie farmaci)</Label>
            <Textarea
              data-testid="student-health-notes"
              rows={2}
              value={form.health_notes}
              onChange={set("health_notes")}
              placeholder="Es. asma lieve, allergia alle api, terapia in corso…"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>Intolleranze / allergie alimentari</Label>
            <Textarea
              data-testid="student-intolerances"
              rows={2}
              value={form.intolerances}
              onChange={set("intolerances")}
              placeholder="Es. celiachia, lattosio, frutta secca…"
              className="mt-1.5"
            />
          </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-stone-100 bg-white shrink-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" data-testid="student-save-btn" disabled={submitting} className="bg-[#1C352D] hover:bg-[#264A3E] transition-colors duration-150">
              {submitting ? "Salvataggio…" : (mode === "edit" ? "Salva modifiche" : "Crea studente")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
